import React, { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Menu,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Radio,
  FormControlLabel,
  RadioGroup,
  DialogContentText,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  FilterList as FilterListIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../config';
import { getFileUrl } from '../utils/urlHelper';
import { format, addMonths, subMonths } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import './Agenda.css';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import Badge from '@mui/material/Badge';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Evento {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  tipo: string;
  paciente_id?: string;
  paciente_nombre?: string;
  descripcion?: string;
  estado: string;
  recordatorios: Recordatorio[];
  color?: string;
  ubicacion?: string;
  notas?: string;
  recurrencia_id?: string;
}

interface Recordatorio {
  tipo: string;
  tiempo_antes: number;
  enviado: boolean;
}

// Colores para cada tipo de evento (acepta 'plan' y 'plan alimentario')
const EVENT_TYPE_COLORS: Record<string, string> = {
  'plan alimentario': '#43a047', // verde
  'plan': '#43a047', // verde (alias)
  'consulta': '#1976d2', // azul
  'seguimiento': '#fbc02d', // amarillo
  'recordatorio': '#e53935', // rojo
  'cumpleaños': '#ab47bc', // violeta
};

// Leyenda de colores: solo mostrar una vez 'Plan Alimentario', no 'Plan'
const EVENT_TYPE_LABELS: Record<string, string> = {
  'plan alimentario': 'Plan Alimentario',
  'consulta': 'Consulta',
  'seguimiento': 'Seguimiento',
  'recordatorio': 'Recordatorio',
  'cumpleaños': 'Cumpleaños',
};

// Componente para el encabezado de los días de la semana en español
const CustomCalendarHeader = () => {
  const weekDays = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  return (
    <Box
      sx={{
        mt: 2.5,
        mb: 1.5,
        display: 'flex',
        justifyContent: 'space-around',
        color: 'text.secondary',
        fontWeight: 'bold',
        fontSize: '1.2rem',
      }}
    >
      {weekDays.map((day) => (
        <Box key={day} sx={{ 
          width: 52, 
          textAlign: 'center' 
        }}>
          {day}
        </Box>
      ))}
    </Box>
  );
};

// Componente de Calendario con nuestro header personalizado
const CalendarNoHeader = (props: any) => (
  <DateCalendar
    {...props}
    slots={{
      ...props.slots,
      toolbar: () => null, // Oculta la barra de mes/año y flechas
      calendarHeader: CustomCalendarHeader, // Usa nuestro encabezado personalizado
    }}
  />
);

// Componente personalizado para renderizar cada día con puntos debajo del número
function CustomDay(props: PickersDayProps<Dayjs> & { eventos?: Evento[] }) {
  const { day, eventos = [], selected, today, ...otherProps } = props;
  const eventosDelDia = eventos.filter(e => e.fecha === day.format('YYYY-MM-DD'));
  const tieneEventos = eventosDelDia.length > 0;
  const pickersDayRef = React.useRef<HTMLButtonElement>(null);

  // Separar cumpleaños de otros eventos
  const cumpleaniosDelDia = eventosDelDia.filter(e => e.tipo === 'cumpleaños');
  const otrosEventosDelDia = eventosDelDia.filter(e => e.tipo !== 'cumpleaños');

  // Agrupar eventos por tipo para mostrar los puntos (excluyendo cumpleaños)
  const puntos = Object.entries(otrosEventosDelDia.reduce((acc, ev) => {
    const tipo = ev.tipo.toLowerCase() === 'plan' ? 'plan alimentario' : ev.tipo.toLowerCase();
    if(EVENT_TYPE_COLORS[tipo]){
      acc[tipo] = (acc[tipo] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>));

  // Organizar los puntos en filas de a 2 (estructura cuadrada)
  const puntosEnFilas = [];
  for (let i = 0; i < puntos.length; i += 2) {
    puntosEnFilas.push(puntos.slice(i, i + 2));
  }
  // Si hay 3 puntos, agrega un Box invisible para cuadrar la segunda fila
  if (puntos.length === 3) {
    puntosEnFilas[1].push(['_vacio', 0]);
  }

  // Tooltip con lista de eventos
  const tooltipContent = tieneEventos ? (
    <Box>
      {eventosDelDia.map((ev) => {
        let tipoMostrar = ev.tipo.toLowerCase();
        if (tipoMostrar === 'plan') tipoMostrar = 'plan alimentario';
        return (
          <Box key={ev.id} sx={{ fontSize: 13, mb: 0.5 }}>
            <span style={{ color: EVENT_TYPE_COLORS[tipoMostrar] || '#888', fontWeight: 700 }}>
              ●
            </span> {ev.paciente_nombre || capitalize(EVENT_TYPE_LABELS[tipoMostrar] || tipoMostrar)}
          </Box>
        );
      })}
    </Box>
  ) : null;

  // Determinar el color del borde y del número
  let borderColor = '#c0c0c0';
  let dayNumberColor = 'inherit';
  let backgroundColor = 'transparent';
  if (selected) {
    borderColor = '#1976d2';
    dayNumberColor = '#1976d2';
  } else if (today) {
    borderColor = '#222';
    backgroundColor = 'rgba(33, 150, 243, 0.08)'; // azul claro, más suave
  }

  // Función para manejar el click en todo el recuadro
  const handleBoxClick = () => {
    // Disparar el click nativo en el PickersDay
    if (pickersDayRef.current) {
      pickersDayRef.current.click();
    }
  };

  return (
    <Tooltip title={tooltipContent} arrow disableInteractive={!tieneEventos}>
      <Box
        onClick={handleBoxClick}
        sx={{
          position: 'relative',
          width: 60,
          height: 80, // La altura total del recuadro no cambia
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: `2px solid ${borderColor}`,
          borderRadius: 2,
          backgroundColor: backgroundColor,
          boxSizing: 'border-box',
          transition: 'border-color 0.2s',
          cursor: 'pointer', // Indicar que es clickeable
          pt: '8px', // Añadimos padding para que el número no esté pegado arriba
        }}
      >
        {/* Número del día con icono de cumpleaños como exponente */}
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <PickersDay 
            {...otherProps} 
            day={day} 
            ref={pickersDayRef}
            sx={{ width: 52, height: 28, minHeight: 0, lineHeight: '28px', fontSize: '1.2rem', zIndex: 1, background: 'none', border: 'none', boxShadow: 'none', padding: 0, pointerEvents: 'none', color: dayNumberColor }} 
          />
          {/* Icono de cumpleaños como exponente */}
          {cumpleaniosDelDia.length > 0 && (
            <CakeIcon 
              sx={{ 
                position: 'absolute',
                top: -6,
                right: 4,
                fontSize: 12,
                color: EVENT_TYPE_COLORS['cumpleaños'],
                zIndex: 2
              }} 
            />
          )}
        </Box>

        {/* Puntos de colores para otros eventos */}
        {otrosEventosDelDia.length > 0 && (
          // ↓↓↓ USAMOS MARGIN-TOP PARA CONTROLAR LA DISTANCIA EXACTA ↓↓↓
          <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '4px', minHeight: 18 }}>
            {puntosEnFilas.map((fila, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
                {fila.map(([tipo, cantidad], i) => (
                  tipo === '_vacio' ? (
                    <Box key={i} sx={{ width: 12, height: 12, visibility: 'hidden' }} />
                  ) : (
                    <Box
                      key={tipo}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: 12, height: 12 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: EVENT_TYPE_COLORS[tipo] || '#888',
                          border: '1.5px solid white',
                        }}
                      />
                      {cantidad > 1 && (
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: EVENT_TYPE_COLORS[tipo] || '#888',
                            position: 'absolute',
                            left: '100%',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cantidad}
                        </Box>
                      )}
                    </Box>
                  )
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

const capitalize = (str: string | undefined | null) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Agenda: React.FC = () => {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);
  const [pacientes, setPacientes] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    fecha: dayjs().format('DD/MM/YYYY'),
    tipo: '',
    paciente_id: '',
    paciente_nombre: '',
    descripcion: '',
    hora_inicio: '',
    hora_fin: '',
  });
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [loading, setLoading] = useState(true);
  const [eventoFiltro, setEventoFiltro] = useState<string>('todos');
  const [anchorFiltro, setAnchorFiltro] = useState<null | HTMLElement>(null);
  const [recurrencia, setRecurrencia] = useState('none');
  const [cantidadRecurrencia, setCantidadRecurrencia] = useState(1);
  const [eventoAEliminar, setEventoAEliminar] = useState<Evento | null>(null);
  const [openDialogEliminarRecurrencia, setOpenDialogEliminarRecurrencia] = useState(false);
  const [openDialogConfirmarEliminacion, setOpenDialogConfirmarEliminacion] = useState(false);
  const [eventoAEliminarSinRecurrencia, setEventoAEliminarSinRecurrencia] = useState<Evento | null>(null);

  useEffect(() => {
    const fechaSeleccionada = localStorage.getItem('agenda_fecha_seleccionada');
    if (fechaSeleccionada) {
      setSelectedDate(dayjs(fechaSeleccionada, 'YYYY-MM-DD'));
      localStorage.removeItem('agenda_fecha_seleccionada');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [eventosRes, pacientesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/eventos`),
          axios.get(`${API_BASE_URL}/pacientes`)
        ]);
        setEventos(eventosRes.data);
        setPacientes(pacientesRes.data);
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchEventos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/eventos`);
      setEventos(response.data);
    } catch (error) {
      console.error('Error al cargar eventos:', error);
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleOpenDialog = (evento?: Evento) => {
    if (evento) {
      setSelectedEvento(evento);
      setFormData({
        fecha: dayjs(evento.fecha).format('DD/MM/YYYY'),
        tipo: evento.tipo,
        paciente_id: evento.paciente_id || '',
        paciente_nombre: evento.paciente_nombre || '',
        descripcion: evento.descripcion || '',
        hora_inicio: evento.hora_inicio || '',
        hora_fin: evento.hora_fin || '',
      });
      
      // Si el evento tiene recurrencia_id, detectar el tipo de recurrencia y cantidad actual
      if (evento.recurrencia_id) {
        const eventosDeLaSerie = eventos.filter(e => e.recurrencia_id === evento.recurrencia_id);
        const eventosOrdenados = eventosDeLaSerie.sort((a, b) => dayjs(a.fecha).diff(dayjs(b.fecha)));
        
        if (eventosOrdenados.length > 1) {
          // Calcular el tipo de recurrencia basado en las fechas
          const fecha1 = dayjs(eventosOrdenados[0].fecha);
          const fecha2 = dayjs(eventosOrdenados[1].fecha);
          const diffDias = fecha2.diff(fecha1, 'day');
          
          if (diffDias === 1) {
            setRecurrencia('diaria');
          } else if (diffDias === 7) {
            setRecurrencia('semanal');
          } else if (diffDias >= 28 && diffDias <= 31) {
            setRecurrencia('mensual');
          } else {
            setRecurrencia('diaria'); // Por defecto
          }
          
          // Establecer la cantidad actual de eventos en la serie
          setCantidadRecurrencia(eventosOrdenados.length);
        } else {
          // Solo queda un evento, no hay recurrencia
          setRecurrencia('none');
          setCantidadRecurrencia(1);
        }
      } else {
        // Evento sin recurrencia
        setRecurrencia('none');
        setCantidadRecurrencia(1);
      }
    } else {
      setSelectedEvento(null);
      setFormData({
        fecha: selectedDate.format('DD/MM/YYYY'),
        tipo: '',
        paciente_id: '',
        paciente_nombre: '',
        descripcion: '',
        hora_inicio: '',
        hora_fin: '',
      });
      // Resetear recurrencia para nuevo evento
      setRecurrencia('none');
      setCantidadRecurrencia(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveEvento = async () => {
    try {
      // Convertir fecha inicio a YYYY-MM-DD
      const fechaInicioISO = dayjs(formData.fecha, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
      
      if (selectedEvento) {
        // EDITAR EVENTO EXISTENTE
        if (recurrencia !== 'none') {
          // Si se agrega recurrencia a un evento existente, crear la serie completa
          let eventosAGuardar = [];
          let recurrencia_id = selectedEvento.recurrencia_id || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          let fechaFinISO = fechaInicioISO;
          
          if (recurrencia === 'diaria') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'day').format('YYYY-MM-DD');
          if (recurrencia === 'semanal') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'week').format('YYYY-MM-DD');
          if (recurrencia === 'mensual') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'month').format('YYYY-MM-DD');
          
          let start = dayjs(fechaInicioISO);
          let end = dayjs(fechaFinISO);
          let current = start;
          while (dayjs(current).isSameOrBefore(end, 'day')) {
            eventosAGuardar.push({
              ...formData,
              id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
              fecha: current.format('YYYY-MM-DD'),
              recurrencia_id,
            });
            if (recurrencia === 'diaria') current = current.add(1, 'day');
            else if (recurrencia === 'semanal') current = current.add(1, 'week');
            else if (recurrencia === 'mensual') current = current.add(1, 'month');
          }
          
          // Si el evento original tenía recurrencia, eliminar todos los eventos de esa serie
          if (selectedEvento.recurrencia_id) {
            const eventosAEliminar = eventos.filter(e => e.recurrencia_id === selectedEvento.recurrencia_id);
            for (const evento of eventosAEliminar) {
              await axios.delete(`${API_BASE_URL}/eventos/${evento.id}`);
            }
          } else {
            // Si no tenía recurrencia, eliminar solo el evento original
            await axios.delete(`${API_BASE_URL}/eventos/${selectedEvento.id}`);
          }
          
          // Crear la nueva serie de eventos
          for (const evento of eventosAGuardar) {
            await axios.post(`${API_BASE_URL}/eventos`, evento);
          }
        } else {
          // Sin recurrencia - actualizar solo el evento original
          const eventoActualizado = {
            ...formData,
            id: selectedEvento.id,
            fecha: fechaInicioISO,
            recurrencia_id: undefined, // Quitar recurrencia si la tenía
          };
          
          await axios.put(`${API_BASE_URL}/eventos/${selectedEvento.id}`, eventoActualizado);
        }
      } else {
        // CREAR NUEVO EVENTO
        let eventosAGuardar = [];
        let recurrencia_id = undefined;
        let fechaFinISO = fechaInicioISO;
        
        if (recurrencia !== 'none') {
          if (recurrencia === 'diaria') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'day').format('YYYY-MM-DD');
          if (recurrencia === 'semanal') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'week').format('YYYY-MM-DD');
          if (recurrencia === 'mensual') fechaFinISO = dayjs(fechaInicioISO).add(cantidadRecurrencia - 1, 'month').format('YYYY-MM-DD');
        }
        
        if (recurrencia !== 'none') {
          recurrencia_id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          let start = dayjs(fechaInicioISO);
          let end = dayjs(fechaFinISO);
          let current = start;
          while (dayjs(current).isSameOrBefore(end, 'day')) {
            eventosAGuardar.push({
              ...formData,
              id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
              fecha: current.format('YYYY-MM-DD'),
              recurrencia_id,
            });
            if (recurrencia === 'diaria') current = current.add(1, 'day');
            else if (recurrencia === 'semanal') current = current.add(1, 'week');
            else if (recurrencia === 'mensual') current = current.add(1, 'month');
          }
        } else {
          eventosAGuardar.push({
            ...formData,
            id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
            fecha: fechaInicioISO,
          });
        }
        
        for (const evento of eventosAGuardar) {
          await axios.post(`${API_BASE_URL}/eventos`, evento);
        }
      }
      
      setOpenDialog(false);
      fetchEventos();
    } catch (error) {
      console.error('Error al guardar evento:', error);
    }
  };

  const handleDeleteEvento = async (evento: Evento) => {
    if (evento.recurrencia_id) {
      setEventoAEliminar(evento);
      setOpenDialogEliminarRecurrencia(true);
    } else {
      // Mostrar diálogo de confirmación para eventos sin recurrencia
      setEventoAEliminarSinRecurrencia(evento);
      setOpenDialogConfirmarEliminacion(true);
    }
  };

  const handleConfirmDeleteEvento = async (opcion: 'uno' | 'atras' | 'adelante' | 'todos') => {
    if (eventoAEliminar) {
      try {
        // console.log('Eliminando evento recurrente:', { opcion, eventoAEliminar });
        
        const eventosDeLaSerie = eventos.filter(e => e.recurrencia_id === eventoAEliminar.recurrencia_id);
        const fechaEventoSeleccionado = dayjs(eventoAEliminar.fecha);
        
        let eventosAEliminar: Evento[] = [];
        
        switch (opcion) {
          case 'uno':
            // Solo este evento
            eventosAEliminar = [eventoAEliminar];
            break;
            
          case 'atras':
            // Eventos hacia atrás (incluyendo el actual)
            eventosAEliminar = eventosDeLaSerie.filter(e => 
              dayjs(e.fecha).isSameOrBefore(fechaEventoSeleccionado, 'day')
            );
            break;
            
          case 'adelante':
            // Eventos hacia adelante (incluyendo el actual)
            eventosAEliminar = eventosDeLaSerie.filter(e => 
              dayjs(e.fecha).isSameOrAfter(fechaEventoSeleccionado, 'day')
            );
            break;
            
          case 'todos':
            // Toda la serie
            eventosAEliminar = eventosDeLaSerie;
            break;
        }
        
        // console.log('Eliminando eventos:', { 
        //   opcion, 
        //   cantidad: eventosAEliminar.length,
        //   eventos: eventosAEliminar.map(e => ({ id: e.id, fecha: e.fecha }))
        // });
        
        for (const evento of eventosAEliminar) {
          // console.log('Eliminando evento:', evento.id);
          await axios.delete(`${API_BASE_URL}/eventos/${evento.id}`);
        }
        
        // console.log('Eventos eliminados correctamente, actualizando lista...');
        await fetchEventos();
        // console.log('Lista actualizada');
        
      } catch (error) {
        console.error('Error al eliminar eventos recurrentes:', error);
      }
      setOpenDialogEliminarRecurrencia(false);
      setEventoAEliminar(null);
    }
  };

  const handleConfirmDeleteEventoSinRecurrencia = async () => {
    if (eventoAEliminarSinRecurrencia) {
      try {
        await axios.delete(`${API_BASE_URL}/eventos/${eventoAEliminarSinRecurrencia.id}`);
        fetchEventos();
        // console.log('Evento eliminado correctamente');
      } catch (error) {
        console.error('Error al eliminar evento:', error);
      }
      setOpenDialogConfirmarEliminacion(false);
      setEventoAEliminarSinRecurrencia(null);
    }
  };

  const handlePrevMonth = () => {
    setSlideDirection('right');
    setCurrentMonth(prev => {
      const nuevoMes = prev.subtract(1, 'month');
      // Si el mes o año cambió, actualiza el selectedDate al primer día del nuevo mes
      if (selectedDate.month() !== nuevoMes.month() || selectedDate.year() !== nuevoMes.year()) {
        setSelectedDate(nuevoMes.startOf('month'));
      }
      return nuevoMes;
    });
  };

  const handleNextMonth = () => {
    setSlideDirection('left');
    setCurrentMonth(prev => {
      const nuevoMes = prev.add(1, 'month');
      // Si el mes o año cambió, actualiza el selectedDate al primer día del nuevo mes
      if (selectedDate.month() !== nuevoMes.month() || selectedDate.year() !== nuevoMes.year()) {
        setSelectedDate(nuevoMes.startOf('month'));
      }
      return nuevoMes;
    });
  };

  // Generar eventos virtuales de cumpleaños
  const eventosCumpleanios = React.useMemo(() => {
    if (!pacientes || pacientes.length === 0) return [];
    const hoy = dayjs();
    // Mostrar cumpleaños solo de pacientes con fecha válida
    return pacientes
      .filter(p => p.fecha_nacimiento && /^\d{2}\/\d{2}\/\d{4}$/.test(p.fecha_nacimiento))
      .map(p => {
        const [dia, mes, anio] = p.fecha_nacimiento.split('/').map(Number);
        // Cumpleaños de este año
        const fechaCumple = dayjs(`${hoy.year()}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`);
        return {
          id: `cumple-${p.id}`,
          fecha: fechaCumple.format('YYYY-MM-DD'),
          hora_inicio: '',
          hora_fin: '',
          tipo: 'cumpleaños',
          paciente_id: p.id,
          paciente_nombre: p.nombre,
          descripcion: '',
          estado: '',
          recordatorios: [],
        };
      });
  }, [pacientes]);
  // Unir eventos reales y virtuales
  const todosLosEventos = React.useMemo(() => {
    // Evitar duplicados si ya hay un evento de cumpleaños real
    const idsCumple = new Set(eventosCumpleanios.map(e => e.id));
    const eventosSinCumpleReales = eventos.filter(e => !(e.tipo === 'cumpleaños' && idsCumple.has(e.id)));
    return [...eventosSinCumpleReales, ...eventosCumpleanios];
  }, [eventos, eventosCumpleanios]);

  const eventosDelDiaSeleccionado = todosLosEventos.filter(
    (evento) => evento.fecha === selectedDate.format('YYYY-MM-DD')
  );

  const handleOpenFiltro = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorFiltro(event.currentTarget);
  };

  const handleCloseFiltro = () => {
    setAnchorFiltro(null);
  };

  const handleSelectFiltro = (tipo: string) => {
    setEventoFiltro(tipo);
    handleCloseFiltro();
  };

  return (
    <Box sx={{ maxWidth: { xs: '100vw', sm: 'none' }, overflowX: { xs: 'hidden', sm: 'visible' }, p: { xs: 1, sm: 2, md: 3 }, pt: { xs: 3, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a202c', mb: 1 }}>
            Agenda
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Calendario de consultas, planes alimentarios y seguimientos nutricionales
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: 2, lg: 4 },
        alignItems: { xs: 'center', lg: 'flex-start' },
        justifyContent: { xs: 'center', lg: 'flex-start' },
        maxWidth: '100%',
        mx: 'auto',
        px: { xs: 2, lg: 6 }
      }}>
        {/* Calendario - Tamaño fijo */}
        <Card sx={{ 
          boxShadow: 6, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e3e9f3 100%)', 
          p: 2,
          width: 480, // Ancho fijo específico
          flexShrink: 0, // Evitar que se comprima
        }}>
          <CardContent sx={{ py: 1.5, px: 2 }}>
            {/* --- 1. BARRA SUPERIOR CON MES Y FLECHAS (LA RESTAURAMOS) --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography 
                variant="h6" 
                sx={{
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#2c3e50',
                  mb: 1.5,
                  textTransform: 'capitalize',
                  letterSpacing: 1,
                  textShadow: '0 2px 8px rgba(25, 118, 210, 0.08)'
                }}
              >
                {format(currentMonth.toDate(), 'MMMM yyyy', { locale: es })}
              </Typography>
              <Box>
                <Tooltip title="Mes anterior">
                  <IconButton onClick={handlePrevMonth} sx={{ mr: 1 }}>
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mes siguiente">
                  <IconButton onClick={handleNextMonth}>
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* --- 2. EL CALENDARIO CORRECTAMENTE CONFIGURADO --- */}
            <div className="agenda-slide-container">
              <SwitchTransition mode="out-in">
                <CSSTransition
                  key={currentMonth.toISOString()}
                  timeout={400}
                  classNames={`slide-${slideDirection}`}
                >
                  <div className="calendar-wrapper" style={{ marginBottom: 32 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                      <DateCalendar
                        value={selectedDate}
                        onChange={handleDateChange}
                        showDaysOutsideCurrentMonth
                        fixedWeekNumber={6}
                        views={["day"]}
                        slots={{
                          day: CustomDay,
                          calendarHeader: CustomCalendarHeader,
                        }}
                        slotProps={{ day: { eventos: todosLosEventos } as any }}
                        sx={{
                          width: '100%',
                          minHeight: 471,
                          '& .MuiDayCalendar-header': {
                            display: 'none',
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            display: 'none',
                          },
                          '& .MuiDayCalendar-root': {
                            height: 'auto !important',
                            overflow: 'visible !important',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          },
                          '& .MuiDayCalendar-weekContainer': {
                            height: 'auto !important',
                            overflow: 'visible !important',
                            justifyContent: 'space-around',
                          },
                          '& .MuiDayCalendar-slideTransition': {
                            height: 'auto !important',
                            overflow: 'visible !important',
                          },
                          '& .MuiPickersSlideTransition-root': {
                            height: 'auto !important',
                            overflow: 'visible !important',
                          },
                          '& .MuiPickersDay-root': {
                            width: 52,
                            height: 28,
                            minHeight: 0,
                            lineHeight: '28px',
                            fontSize: '1.2rem',
                            zIndex: 1,
                            background: 'none',
                            border: 'none',
                            boxShadow: 'none',
                            padding: 0,
                          },
                          // Días fuera del mes actual - más claros
                          '& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth': {
                            color: 'rgba(0, 0, 0, 0.38)',
                          },
                          // Día seleccionado: recuadro de color primario, sin relleno
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'transparent',
                            border: '2px solid #1976d2',
                            color: '#1976d2',
                          },
                          // Día de hoy: recuadro de color gris, sin relleno
                          '& .MuiPickersDay-root.MuiPickersDay-today': {
                            backgroundColor: 'transparent',
                            border: '2px solid #222',
                            color: '#222',
                          },
                          // Si es hoy y seleccionado, que prime el color seleccionado
                          '& .MuiPickersDay-root.Mui-selected.MuiPickersDay-today': {
                            border: '2.5px solid #1976d2',
                            color: '#1976d2',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </div>

            {/* --- 3. LEYENDA DE COLORES (SIN CAMBIOS) --- */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              mt: 0.2, 
              justifyContent: 'center', 
              borderTop: '1px solid #aaa', 
              pt: 3, 
              pb: 0, 
              mb: 0 
            }}>
              {Object.entries(EVENT_TYPE_LABELS).map(([tipo, label]) => (
                <Box key={tipo} sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                  {tipo === 'cumpleaños' ? (
                    <CakeIcon sx={{ fontSize: 16, color: EVENT_TYPE_COLORS[tipo], mr: 1, display: 'flex', alignItems: 'center' }} />
                  ) : (
                    <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: EVENT_TYPE_COLORS[tipo], mr: 1, display: 'flex', alignItems: 'center' }} />
                  )}
                  <Typography variant="caption" sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center' }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Card de eventos - Se va abajo en pantallas pequeñas */}
        <Card sx={{ 
          maxWidth: 420, 
          boxShadow: 2,
          flexShrink: 0
        }}>
          <CardContent sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              border: '1px solid rgba(25, 118, 210, 0.2)',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                m: 0, 
                color: '#2c3e50'
              }}>
                {`Eventos del ${selectedDate.format('DD/MM/YYYY')}`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Crear evento">
                  <IconButton 
                    onClick={() => handleOpenDialog()} 
                    sx={{ 
                      borderRadius: '50%',
                      color: 'primary.main',
                      bgcolor: 'transparent',
                      width: 32,
                      height: 32,
                      '&:hover': { 
                        bgcolor: 'primary.main',
                        color: 'white',
                        transform: 'scale(1.1)',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={eventoFiltro === 'todos' ? 'Filtrar eventos' : `Filtro activo: ${EVENT_TYPE_LABELS[eventoFiltro]}` }>
                  <IconButton onClick={handleOpenFiltro} sx={{ borderRadius: 2 }}>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={eventoFiltro === 'todos'}
                      overlap="circular"
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <FilterListIcon color={eventoFiltro === 'todos' ? 'inherit' : 'primary'} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Menu
              anchorEl={anchorFiltro}
              open={Boolean(anchorFiltro)}
              onClose={handleCloseFiltro}
              MenuListProps={{ dense: true }}
            >
              <MenuItem onClick={() => handleSelectFiltro('todos')} selected={eventoFiltro === 'todos'}>
                <ListItemIcon><Radio checked={eventoFiltro === 'todos'} size="small" /></ListItemIcon>
                <ListItemText primary="Todos" />
              </MenuItem>
              {Object.entries(EVENT_TYPE_LABELS)
                .filter(([tipo]) => tipo !== 'cumpleaños') // Excluir cumpleaños del selector
                .map(([tipo, label]) => (
                <MenuItem key={tipo} onClick={() => handleSelectFiltro(tipo)} selected={eventoFiltro === tipo}>
                  <ListItemIcon><Radio checked={eventoFiltro === tipo} size="small" style={{ color: EVENT_TYPE_COLORS[tipo] }} /></ListItemIcon>
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Menu>

            {(() => {
              let eventosFiltrados = eventosDelDiaSeleccionado;
              if (eventoFiltro !== 'todos') {
                eventosFiltrados = eventosFiltrados.filter(ev => {
                  let tipo = ev.tipo.toLowerCase();
                  if (tipo === 'plan') tipo = 'plan alimentario';
                  return tipo === eventoFiltro;
                });
              }
              // Ordenar por hora de inicio (formato 'HH:mm')
              eventosFiltrados = [...eventosFiltrados].sort((a, b) => {
                if (!a.hora_inicio && !b.hora_inicio) return 0;
                if (!a.hora_inicio) return 1;
                if (!b.hora_inicio) return -1;
                return a.hora_inicio.localeCompare(b.hora_inicio);
              });
              if (eventosFiltrados.length === 0) {
                const hayFiltro = eventoFiltro !== 'todos';
                return (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {hayFiltro ? 'No hay eventos para el filtro seleccionado.' : 'No hay eventos para este día.'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      {hayFiltro && (
                        <Button variant="outlined" color="inherit" onClick={() => setEventoFiltro('todos')}>
                          Quitar filtro
                        </Button>
                      )}
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Crear evento
                      </Button>
                    </Box>
                  </Box>
                );
              }
              return (
                <List disablePadding>
                  {eventosFiltrados.map((ev, index) => {
                    let tipo = ev.tipo.toLowerCase();
                    if (tipo === 'plan') tipo = 'plan alimentario';
                    return (
                      <Fragment key={ev.id}>
                        <ListItem
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            p: 2,
                            backgroundColor: '#fafbfc',
                            border: '1px solid #e8eaed',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              background: '#f5f7fa',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              transform: 'translateY(-1px)'
                            },
                            alignItems: 'flex-start',
                          }}
                          secondaryAction={
                            ev.tipo === 'cumpleaños' ? null : (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Editar">
                                  <IconButton 
                                    edge="end" 
                                    aria-label="editar" 
                                    onClick={() => handleOpenDialog(ev)} 
                                    sx={{ 
                                      opacity: 0.8,
                                      transition: 'all 0.2s',
                                      color: 'primary.main',
                                      '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton 
                                    edge="end" 
                                    aria-label="eliminar" 
                                    onClick={() => handleDeleteEvento(ev)} 
                                    sx={{ 
                                      opacity: 0.8,
                                      transition: 'all 0.2s',
                                      color: 'error.main',
                                      ml: 1.5,
                                      '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                {ev.tipo === 'cumpleaños' ? (
                                  <CakeIcon sx={{ color: EVENT_TYPE_COLORS['cumpleaños'], mr: 1.5, fontSize: 22, display: 'flex', alignItems: 'center' }} />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      bgcolor: EVENT_TYPE_COLORS[tipo],
                                      mr: 1.5,
                                      flexShrink: 0,
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  />
                                )}
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#1a1a1a',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {ev.tipo === 'cumpleaños' ? `Cumpleaños de ${ev.paciente_nombre}` : capitalize(EVENT_TYPE_LABELS[ev.tipo])}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ pl: 3.5 }}>
                                {ev.paciente_nombre && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500, 
                                      color: '#2c3e50',
                                      mb: 0.5,
                                      fontSize: '0.95rem'
                                    }}
                                  >
                                    {ev.paciente_nombre}
                                  </Typography>
                                )}
                                {ev.hora_inicio && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#5a6c7d',
                                      fontSize: '0.9rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 0.5
                                    }}
                                  >
                                    <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.7 }} />
                                    {ev.hora_inicio}
                                    {ev.hora_fin ? ` - ${ev.hora_fin}` : ''}
                                  </Typography>
                                )}
                                {ev.descripcion && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#5a6c7d',
                                      fontSize: '0.9rem',
                                      mt: 1,
                                      lineHeight: 1.4
                                    }}
                                  >
                                    {ev.descripcion}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < eventosFiltrados.length - 1 && <Divider sx={{ my: 1 }} />}
                      </Fragment>
                    );
                  })}
                </List>
              );
            })()}
          </CardContent>
        </Card>
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedEvento ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="tipo-label">Tipo</InputLabel>
              <Select
                labelId="tipo-label" value={formData.tipo} label="Tipo"
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              >
                {Object.entries(EVENT_TYPE_LABELS)
                  .filter(([tipo]) => tipo !== 'cumpleaños') // Excluir cumpleaños del selector
                  .map(([tipo, label]) => (
                  <MenuItem key={tipo} value={tipo}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: EVENT_TYPE_COLORS[tipo],
                          mr: 1.5,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>{label}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={pacientes}
              getOptionLabel={(option) => option.nombre || ''}
              value={pacientes.find(p => p.id === formData.paciente_id) || null}
              onChange={(_, value) => setFormData({ ...formData, paciente_id: value ? value.id : '', paciente_nombre: value ? value.nombre : '' })}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                // Si no hay término de búsqueda, mostrar todos los pacientes ordenados alfabéticamente
                if (!searchTerm.trim()) {
                  return options.sort((a, b) => 
                    (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
                  );
                }
                // Si hay término de búsqueda, filtrar y ordenar
                return options.filter(option => {
                  const nombre = (option.nombre || '').toLowerCase();
                  const email = (option.email || '').toLowerCase();
                  const email2 = (option.email2 || '').toLowerCase();
                  return nombre.includes(searchTerm) || 
                         email.includes(searchTerm) ||
                         email2.includes(searchTerm);
                }).sort((a, b) => 
                  (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar
                    src={getFileUrl(option?.ruta_imagen_perfil)}
                    sx={{ width: 28, height: 28, mr: 1 }}
                  >
                    {option.nombre ? (
                      <PersonIcon />
                    ) : null}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.nombre || 'Sin nombre'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.email}
                    </Typography>
                    {option.email2 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'grey.600',
                          fontSize: '0.7rem',
                          fontStyle: 'italic',
                          display: 'block'
                        }}
                      >
                        {option.email2}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Paciente" 
                  fullWidth 
                  sx={{ mb: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha"
                value={formData.fecha ? dayjs(formData.fecha, ['YYYY-MM-DD', 'DD/MM/YYYY']) : null}
                format="DD/MM/YYYY"
                onChange={date => {
                  setFormData({ ...formData, fecha: date ? dayjs(date).format('DD/MM/YYYY') : '' });
                }}
                slotProps={{ textField: { fullWidth: true, sx: { mb: 2 }, InputLabelProps: { shrink: true } } }}
              />
            </LocalizationProvider>
            <TextField
              label="Hora de inicio"
              type="time"
              fullWidth
              sx={{ mb: 2 }}
              value={formData.hora_inicio || ''}
              onChange={e => setFormData({ ...formData, hora_inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              label="Hora de fin"
              type="time"
              fullWidth
              sx={{ mb: 2 }}
              value={formData.hora_fin || ''}
              onChange={e => setFormData({ ...formData, hora_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              label="Descripción"
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              value={formData.descripcion || ''}
              onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
            />
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <RadioGroup
                row
                value={recurrencia}
                onChange={e => setRecurrencia(e.target.value)}
                name="recurrencia"
              >
                <FormControlLabel value="none" control={<Radio size="small" />} label="Sin recurrencia" />
                <FormControlLabel value="diaria" control={<Radio size="small" />} label="Diaria" />
                <FormControlLabel value="semanal" control={<Radio size="small" />} label="Semanal" />
                <FormControlLabel value="mensual" control={<Radio size="small" />} label="Mensual" />
              </RadioGroup>
            </FormControl>
            {recurrencia !== 'none' && (
              <TextField
                label={`Cantidad de ${recurrencia === 'diaria' ? 'días' : recurrencia === 'semanal' ? 'semanas' : 'meses'}`}
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                value={cantidadRecurrencia}
                onChange={e => setCantidadRecurrencia(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: 60 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveEvento} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialogEliminarRecurrencia} onClose={() => setOpenDialogEliminarRecurrencia(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Eliminar evento recurrente</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Este evento es parte de una serie recurrente. ¿Qué eventos quieres eliminar?
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button 
              variant="outlined" 
              onClick={() => handleConfirmDeleteEvento('uno')}
              sx={{ 
                justifyContent: 'flex-start', 
                textAlign: 'left', 
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'primary.main'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Solo este evento</Typography>
                <Typography variant="caption" color="text.secondary">
                  Elimina únicamente el evento seleccionado
                </Typography>
              </Box>
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => handleConfirmDeleteEvento('atras')}
              sx={{ 
                justifyContent: 'flex-start', 
                textAlign: 'left', 
                py: 1.5, 
                borderColor: 'warning.main', 
                color: 'warning.main',
                '&:hover': {
                  backgroundColor: 'rgba(237, 108, 2, 0.08)',
                  borderColor: 'warning.dark'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Eventos hacia atrás</Typography>
                <Typography variant="caption" color="text.secondary">
                  Elimina este evento y todos los anteriores de la serie
                </Typography>
              </Box>
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => handleConfirmDeleteEvento('adelante')}
              sx={{ 
                justifyContent: 'flex-start', 
                textAlign: 'left', 
                py: 1.5, 
                borderColor: 'warning.main', 
                color: 'warning.main',
                '&:hover': {
                  backgroundColor: 'rgba(237, 108, 2, 0.08)',
                  borderColor: 'warning.dark'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Eventos hacia adelante</Typography>
                <Typography variant="caption" color="text.secondary">
                  Elimina este evento y todos los siguientes de la serie
                </Typography>
              </Box>
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => handleConfirmDeleteEvento('todos')}
              sx={{ 
                justifyContent: 'flex-start', 
                textAlign: 'left', 
                py: 1.5, 
                borderColor: 'error.main', 
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  borderColor: 'error.dark'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Toda la serie</Typography>
                <Typography variant="caption" color="text.secondary">
                  Elimina todos los eventos de la serie recurrente
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogEliminarRecurrencia(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eventos sin recurrencia */}
      <Dialog 
        open={openDialogConfirmarEliminacion} 
        onClose={() => setOpenDialogConfirmarEliminacion(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            ¿Está seguro que desea eliminar este evento? Esta acción no se puede deshacer.
          </DialogContentText>
          {eventoAEliminarSinRecurrencia && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {eventoAEliminarSinRecurrencia.paciente_nombre || capitalize(eventoAEliminarSinRecurrencia.tipo)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dayjs(eventoAEliminarSinRecurrencia.fecha).format('DD/MM/YYYY')} - {eventoAEliminarSinRecurrencia.hora_inicio} a {eventoAEliminarSinRecurrencia.hora_fin}
              </Typography>
              {eventoAEliminarSinRecurrencia.descripcion && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {eventoAEliminarSinRecurrencia.descripcion}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogConfirmarEliminacion(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDeleteEventoSinRecurrencia} 
            color="error" 
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agenda; 